<template>
  <nav class="top-nav" :class="{ 'top-nav--sg': shouldHideLocation }">
    <!-- Strefa lewej kolumny: zakładki startują na osi prawej krawędzi logo,
         wspólnej dla headera, nawigacji i sidebara (--left-column-width). -->
    <div class="top-nav__gutter" aria-hidden="true"></div>

    <!-- Link do bieżących pomiarów (Current) widoczny tylko jeśli użytkownik ma odpowiedni dostęp -->
    <router-link
      v-if="hasAccess('CurrentMeasurements') && !shouldHideLocation"
      to="/"
      class="top-nav__tab"
      :class="{ 'router-link-active': isCurrentMeasurementActive, 'router-link-exact-active': isCurrentMeasurementActive }"
    >
      <!-- Liczba nowych pojazdów w nawiasie tylko, gdy nie jesteśmy na głównej stronie -->
      {{ t('current-measurement').toUpperCase() }} {{ newCarsCount > 0 && $route.path !== '/' ? `(${newCarsCount})` : '' }}
    </router-link>

    <!-- Link do archiwum -->
    <router-link
      v-if="hasAccess('ArchiveMeasurements')"
      to="/archival"
      class="top-nav__tab"
      :class="{ 'router-link-active': isArchiveActive, 'router-link-exact-active': isArchiveActive }"
    >
      {{ t('archive').toUpperCase() }}
    </router-link>

    <!-- Statystyki -->
    <router-link v-if="hasAccess('Stat')" to="/stat" class="top-nav__tab">
      {{ t('statistics').toUpperCase() }}
    </router-link>

    <!-- VMS (znaki zmiennej treści) -->
    <router-link
      v-if="hasAccess('Vms') && !shouldHideLocation"
      to="/vms"
      class="top-nav__tab"
    >
      {{ t('vms').toUpperCase() }}
    </router-link>

    <!-- Rozwijane menu administracyjne (np. zarządzanie użytkownikami, zgłoszenia, białe/czarne listy) -->
    <div
      class="top-nav__tab top-nav__dropdown"
      :class="{ 'is-open': isManagementOpen, 'is-dismissed': isManagementDismissed, 'is-active': isParentActive(['/user-management', '/user-activity', '/role-group-management', '/location-management', '/calibration', '/calibration-deviation', '/location-statuses', '/black-list', '/white-list', '/contact']) }"
      v-if="canShowManagement"
      @mouseleave="clearManagementDismiss"
      @focusin="clearManagementDismiss"
    >
      <button
        type="button"
        class="top-nav__dropdown-label"
        :aria-expanded="isManagementOpen ? 'true' : 'false'"
        @click.stop="toggleManagement()"
      >
        {{ t('management').toUpperCase() }}
      </button>
      <ul class="top-nav__menu" @click="onMenuLinkClick">
        <li>
          <!-- Zarządzanie użytkownikami -->
          <router-link
            to="/user-management"
            :class="{ 'router-link-active': isActive('/user-management') }"
            class="top-nav__menu-link"
            v-if="hasAccess('UserManagement')"
          >
          {{ t('user-management').toUpperCase() }}
          </router-link>
        </li>
        <li>
          <!-- Aktywność użytkowników — wymaga ROLE_5 lub ROLE_6 (osobne uprawnienie
               do logów aktywności, NIE wynika z samego ROLE_7). -->
          <router-link
            to="/user-activity"
            :class="{ 'router-link-active': isActive('/user-activity') }"
            class="top-nav__menu-link"
            v-if="hasAccess('UserActivity')"
          >
          {{ t('user-activity').toUpperCase() }}
          </router-link>
        </li>
        <li>
          <!-- Zarządzanie rolami i grupami — wymaga ROLE_6 (pełny role-manager).
               ROLE_5 daje tylko podgląd ról dla potrzeb przypisania userowi. -->
          <router-link
            to="/role-group-management"
            :class="{ 'router-link-active': isActive('/role-group-management') }"
            class="top-nav__menu-link"
            v-if="hasAccess('RoleManagement')"
          >
          {{ t('role-group-management').toUpperCase() }}
          </router-link>
        </li>
        <li>
          <!-- Zarządzanie lokalizacjami — wymaga ROLE_10 oraz włączonej flagi funkcji
               (APP_FEATURE_LOCATION_MANAGEMENT_ENABLED po stronie backendu). -->
          <router-link
            to="/location-management"
            :class="{ 'router-link-active': isActive('/location-management') }"
            class="top-nav__menu-link"
            v-if="canShowLocationManagement"
          >
          {{ t('location-management').toUpperCase() }}
          </router-link>
        </li>
        <li>
          <!-- Zgłoszenia (ticket) -->
          <router-link
            to="/tickets"
            :class="{ 'router-link-active': isActive('/tickets') }"
            class="top-nav__menu-link"
            v-if="hasAccess('ReportsManagement') && !shouldHideLocation"
          >
          {{ t('reports').toUpperCase() }}
          </router-link>
        </li>

        <!-- Kalibracja wag (diagnostyka) — ROLE_13. Pozycja jest klikalnym linkiem do
             /calibration. Podmenu z dewiacją (ROLE_14) rozwija się po najechaniu — bez
             ROLE_14 pozycja jest zwykłą zakładką, więc NIE dostaje ani klasy
             `--parent` (strzałka „›”), ani kontenera podmenu. -->
        <li
          v-if="canShowCalibration"
          :class="{ 'top-nav__submenu-parent': canShowCalibrationDeviation }"
        >
          <router-link
            to="/calibration"
            :class="{
              'router-link-active': isParentActive(['/calibration', '/calibration-deviation']),
              'top-nav__menu-link--parent': canShowCalibrationDeviation,
            }"
            class="top-nav__menu-link"
          >
          {{ t('calibration').toUpperCase() }}
          </router-link>
          <ul class="top-nav__submenu" v-if="canShowCalibrationDeviation">
            <li>
              <!-- Dewiacja (odchył) kalibracji wag — ROLE_14 -->
              <router-link
                to="/calibration-deviation"
                :class="{ 'router-link-active': isActive('/calibration-deviation') }"
                class="top-nav__menu-link"
              >
              {{ t('calibration-deviation').toUpperCase() }}
              </router-link>
            </li>
          </ul>
        </li>

        <!-- Sama dewiacja kalibracji (ROLE_14 bez ROLE_13) — nie ma pozycji nadrzędnej,
             pod którą mogłaby się rozwinąć, więc pokazujemy ją jako zwykłą zakładkę. -->
        <li v-else-if="canShowCalibrationDeviation">
          <router-link
            to="/calibration-deviation"
            :class="{ 'router-link-active': isActive('/calibration-deviation') }"
            class="top-nav__menu-link"
          >
          {{ t('calibration-deviation').toUpperCase() }}
          </router-link>
        </li>

        <li>
          <!-- Historia statusów lokalizacji -->
          <router-link
            to="/location-statuses"
            :class="{ 'router-link-active': isActive('/location-statuses') }"
            class="top-nav__menu-link"
            v-if="hasAccess('LocationStatusHistory')"
          >
          {{ t('location-statuses-history').toUpperCase() }}
          </router-link>
        </li>

        <!-- Pojazdy monitorowane (submenu: czarna/biała lista, kontakty) -->
        <li class="top-nav__submenu-parent">
          <span
            :class="{ 'router-link-active': isParentActive(['/black-list', '/white-list', '/contact']) }"
            class="top-nav__menu-link top-nav__menu-link--parent"
            v-if="hasAccess('WantedList')"
          >
          {{ t('monitored-vehicles').toUpperCase() }}
          </span>
          <ul class="top-nav__submenu">
            <li>
              <!-- Czarna lista -->
              <router-link
                to="/black-list"
                :class="{ 'router-link-active': isActive('/black-list') }"
                class="top-nav__menu-link"
                v-if="hasAccess('BlackList')"
              >
              {{ t('black-list').toUpperCase() }}
              </router-link>
            </li>
            <li>
              <!-- Biała lista -->
              <router-link
                to="/white-list"
                :class="{ 'router-link-active': isActive('/white-list') }"
                class="top-nav__menu-link"
                v-if="hasAccess('WhiteList')"
              >
              {{ t('white-list').toUpperCase() }}
              </router-link>
            </li>
            <li>
              <!-- Kontakty (np. z pojazdami) -->
              <router-link
                to="/contact"
                :class="{ 'router-link-active': isActive('/contact') }"
                class="top-nav__menu-link"
                v-if="hasAccess('Contacts')"
              >
              {{ t('contacts').toUpperCase() }}
              </router-link>
            </li>
          </ul>
        </li>
      </ul>
    </div>

    <!-- Przełącznik języka (flaga + menu z innymi językami) -->
    <div class="top-nav__lang">
      <span :title="currentLang.label" class="top-nav__flag">
        <img :src="currentLang.flag" alt="flag" />
      </span>
      <div class="top-nav__lang-menu">
        <span v-for="lang in otherLanguages" :key="lang.code"
          :title="lang.label"
          @click="selectLanguage(lang.code)">
          <img :src="lang.flag" alt="flag" />
        </span>
      </div>
    </div>
  </nav>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useUserStore } from "@/stores/user.store";
import { useConfigStore } from "@/stores/config.store";
import CurrentMeasurementsService from '../services/CurrentMeasurementsService.js';
import { useI18n } from 'vue-i18n';
import { isSG } from "@/config/appVersion";

export default {
  setup() {
    const route = useRoute();
    const userStore = useUserStore();
    const configStore = useConfigStore();
    const { locale } = useI18n();

    // Sprawdza, czy użytkownik ma dostęp do określonej funkcji/roli
    const hasAccess = (id) => userStore.hasAccess(id);

    // Rozwinięcie menu „Zarządzanie". Obok :hover obsługujemy też klik i
    // klawiaturę (:focus-within w CSS) — samo `hover` bywało nieużyteczne.
    const isManagementOpen = ref(false);
    // Wybór pozycji z menu musi je natychmiast schować, mimo że kliknięty link
    // zostaje z focusem (`:focus-within`), a kursor nadal wisi nad listą (`:hover`) —
    // bez tej flagi lista wyglądała na „przypiętą" do kliknięcia gdzie indziej.
    const isManagementDismissed = ref(false);
    const toggleManagement = () => {
      isManagementDismissed.value = false;
      isManagementOpen.value = !isManagementOpen.value;
    };
    const closeManagement = () => { isManagementOpen.value = false; };
    const clearManagementDismiss = () => { isManagementDismissed.value = false; };

    // Klik w pozycję menu (także w podmenu „Monitorowane pojazdy") — zamknij listę.
    const onMenuLinkClick = (event) => {
      if (!event.target.closest("a")) return; // rozwijacz podmenu (<span>) nie zamyka
      isManagementOpen.value = false;
      isManagementDismissed.value = true;
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    };

    // Liczba nowych pojazdów (reaguje na zmiany)
    const newCarsCount = ref(CurrentMeasurementsService.getNewCarsNumber());

    // Aktualizuje liczbę nowych pojazdów co chwilę
    const updateNewCarsCount = () => { newCarsCount.value = CurrentMeasurementsService.getNewCarsNumber(); };

    // Aktywność linków (podświetlenie, pogrubienie, itp.)
    const isActive = (path) => route.path === path;
    const isCurrentMeasurementActive = computed(() => route.path === '/' || route.path.includes('/vehicle/') && !route.path.includes('vehicleArch'));
    const isArchiveActive = computed(() => route.path === '/archival' || route.path.includes('/vehicleArch/'));

    // Czy dany "parent" dropdown jest aktywny (dla podświetlenia całego bloku menu)
    const isParentActive = (paths) => {
      const adminPaths = ['/user-management', '/user-activity', '/role-group-management', '/location-management', '/calibration', '/calibration-deviation', '/location-statuses', '/black-list', '/white-list', '/contact'];
      if (!adminPaths.includes(route.path)) return false;
      return paths.includes(route.path);
    };

    // Definicja dostępnych języków (z flagami)
    const languages = [
      { code: 'pl', label: 'Polski', flag: require('@/assets/flags/pl.png') },
      { code: 'en', label: 'English', flag: require('@/assets/flags/uk.svg') },
      { code: 'ua', label: 'Україна', flag: require('@/assets/flags/ua.png') },
      { code: 'lt', label: 'Lietuvių', flag: require('@/assets/flags/lt.png') },
      { code: 'de', label: 'Deutsch', flag: require('@/assets/flags/de.png') },
      { code: 'ro', label: 'Română', flag: require('@/assets/flags/ro.png') },
    ];

    // Wybrany język i pozostałe do wyboru
    const currentLang = computed(() => languages.find(l => l.code === locale.value) || languages[0]);
    const otherLanguages = computed(() => languages.filter(l => l.code !== locale.value));
    function selectLanguage(code) {
      locale.value = code;
      localStorage.setItem('lang', code);
    }

    const shouldHideLocation = computed(() => isSG);

    // Widoczność pozycji „Zarządzanie lokalizacjami" — wymaga uprawnienia ROLE_10 ORAZ włączonej
    // flagi funkcji (parametryzowanej po stronie backendu w .env/.env.local).
    const canShowLocationManagement = computed(
      () => hasAccess('LocationManagement') && configStore.featureFlags.locationManagement
    );

    // Widoczność pozycji „Kalibracja" — ROLE_13 (PermissionCode::CALIBRATION_MANAGEMENT).
    // W wariancie SG diagnostyka jest ukryta.
    const canShowCalibration = computed(
      () => hasAccess('CalibrationManagement') && !shouldHideLocation.value
    );

    // Widoczność podpozycji „Dewiacja kalibracji" — niezależne ROLE_14
    // (PermissionCode::CALIBRATION_DEVIATION_MANAGEMENT). Decyduje też o tym, czy pozycja
    // „Kalibracja" w ogóle zachowuje się jak rozwijana (strzałka „›" + podmenu).
    const canShowCalibrationDeviation = computed(
      () => hasAccess('CalibrationDeviation') && !shouldHideLocation.value
    );

    // Widoczność wrappera "Zarządzanie" — pokrywa się z faktycznymi warunkami v-if pozycji
    // wewnątrz dropdownu. Wrapper ukryty, jeśli user ma uprawnienia tylko do pozycji, które
    // są odgórnie wyłączone (wariant projektu, feature flag itd.) — żeby nie pojawiał się
    // pusty dropdown. Przy dodawaniu nowej pozycji do dropdownu aktualizuj oba miejsca:
    // template (v-if pozycji) i ten computed (gałąź odpowiadającą tej pozycji).
    const canShowManagement = computed(() => {
      // user-management — ROLE_7 + ROLE_5 lub ROLE_6 (czyli pełny dostęp do zarządzania użytkownikami)
      if (hasAccess('UserManagement')) return true;
      // user-activity — niezależne uprawnienie ROLE_8 + ROLE_5 lub ROLE_6 (czyli pełny dostęp do zarządzania użytkownikami)
      if (hasAccess('UserActivity')) return true;
      // role-group-management — niezależne uprawnienie ROLE_9
      if (hasAccess('RoleManagement')) return true;
      // location-management — ROLE_10 + włączona flaga funkcji
      if (canShowLocationManagement.value) return true;
      // zgłoszenia (zakładka) — ROLE_12, ukryte w wariantach SG. ROLE_11 (sama wysyłka
      // zgłoszenia ikoną „!”) NIE otwiera dropdownu „Zarządzanie”.
      if (hasAccess('ReportsManagement') && !shouldHideLocation.value) return true;
      // calibration - ROLE_13 ukryte w wariantach SG
      if (canShowCalibration.value) return true;
      // calibration-deviation - ROLE_14 ukryte w wariantach SG
      if (canShowCalibrationDeviation.value) return true;
      // location-statuses-history - ROLE_15
      if (hasAccess('LocationStatusHistory')) return true;
      // location-statuses-history-router - ROLE_16
      if (hasAccess('LocationStatusHistoryRouter')) return true;
      // black-list / white-list / contact — bez gating'u wariantu
      if (hasAccess('BlackList')) return true;
      if (hasAccess('WhiteList')) return true;
      if (hasAccess('Contacts')) return true;
      
      return false;
    });

    // Po zamontowaniu komponentu, co 10ms aktualizuj liczbę nowych pojazdów i ustaw język z localStorage (jeśli jest)
    onMounted(() => {
      updateNewCarsCount();
      const interval = setInterval(updateNewCarsCount, 10);
      onBeforeUnmount(() => clearInterval(interval));
      const savedLang = localStorage.getItem('lang');
      if (savedLang && locale.value !== savedLang) {
        locale.value = savedLang;
      }
      // Klik poza paskiem zamyka rozwinięte menu.
      document.addEventListener("click", closeManagement);
      onBeforeUnmount(() => document.removeEventListener("click", closeManagement));

      // Flagi funkcji przyjeżdżają razem z profilem (`/users/self`) i normalnie są już w
      // store'ze, zanim menu się zamontuje — dlatego menu jest kompletne od pierwszego
      // renderu. Tu zostaje tylko domknięcie brzegowych przypadków: czekamy na profil
      // (idempotentny), a osobne żądanie o flagi robimy wyłącznie wtedy, gdy backend
      // nie odesłał ich w profilu (starsza wersja API) — wtedy `loadFeatureFlags`
      // nie jest no-opem.
      userStore.self().catch(() => {}).finally(() => configStore.loadFeatureFlags());
    });

    // Zmiana trasy (wybór pozycji z menu) też je zamyka.
    watch(() => route.path, closeManagement);

    return {
      hasAccess, newCarsCount, isActive, isParentActive, isCurrentMeasurementActive, isArchiveActive,
      languages, currentLang, otherLanguages, selectLanguage, shouldHideLocation, canShowManagement,
      canShowLocationManagement, canShowCalibration, canShowCalibrationDeviation,
      isManagementOpen, toggleManagement,
      isManagementDismissed, clearManagementDismiss, onMenuLinkClick
    };
  },
};
</script>

<style scoped>
/* Globalna nawigacja: równomiernie rozłożone zakładki na pełną szerokość,
   bez sztywnych szerokości w px (dostosowuje się do liczby dostępnych pozycji). */
.top-nav {
  display: flex;
  align-items: stretch;
  min-height: var(--nav-height);
  background: var(--surface-secondary);
  border-bottom: 1px solid var(--border-default);
  /* Rozwijane menu musi wychodzić poza pasek — żadnego przycinania. */
  overflow: visible;
}

/* Strefa lewej kolumny w pasku nawigacji — wizualnie kontynuacja sidebara,
   dzięki niej pierwsza zakładka startuje na osi prawej krawędzi logo. */
.top-nav__gutter {
  flex: 0 0 var(--left-column-width);
  width: var(--left-column-width);
  min-width: 0;
  background: var(--surface-secondary);
  border-right: 1px solid var(--border-default);
}

.top-nav__tab {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 0;
  min-width: 0;
  padding: 14px var(--spacing-sm);
  border-right: 1px solid var(--border-default);
  background: var(--surface-secondary);
  color: var(--text-secondary);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
  letter-spacing: var(--letter-spacing-caps);
  text-align: center;
  text-decoration: none;
  white-space: nowrap;
  text-overflow: ellipsis;
  transition: background-color var(--transition-fast), color var(--transition-fast);
}

/* Przycinanie tekstu dotyczy tylko zwykłych zakładek — pozycja z rozwijanym
   menu musi pozostać `overflow: visible`, inaczej menu jest ucinane. */
a.top-nav__tab {
  overflow: hidden;
}

.top-nav__tab:hover {
  background: var(--surface-hover);
  color: var(--text-primary);
  text-decoration: none;
}

/* Aktywna zakładka: biała powierzchnia + jednoznaczny wskaźnik u dołu. */
.top-nav__tab.router-link-active,
.top-nav__dropdown.is-active {
  background: var(--surface-primary);
  color: var(--text-primary);
  font-weight: var(--font-weight-bold);
  box-shadow: inset 0 -3px 0 var(--surface-inverse);
}

.top-nav__tab:focus-visible {
  outline: 2px solid var(--border-focus);
  outline-offset: -2px;
}

/* --- rozwijane „Zarządzanie" ------------------------------------------------ */

.top-nav__dropdown {
  position: relative;
  cursor: pointer;
}

/* Etykieta rozwijanego menu jest przyciskiem (klik + klawiatura), ale wygląda
   dokładnie jak pozostałe zakładki — zerujemy domyślny wygląd przycisku. */
.top-nav__dropdown-label {
  display: block;
  width: 100%;
  min-height: 0;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  text-transform: none;
  text-align: center;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
}

.top-nav__menu {
  position: absolute;
  top: 100%;
  /* Zakładka „Zarządzanie" jest przy prawej krawędzi aplikacji, więc menu
     rozwijamy w lewo — nie wychodzi poza kolumnę 1300 px. */
  right: -1px;
  left: auto;
  z-index: 300;
  display: none;
  min-width: 100%;
  width: max-content;
  max-width: 320px;
  margin: 0;
  padding: var(--spacing-xs) 0;
  list-style: none;
  background: var(--surface-primary);
  border: 1px solid var(--border-default);
  border-radius: 0 0 var(--panel-radius) var(--panel-radius);
  box-shadow: var(--shadow-overlay);
}

.top-nav__dropdown:hover .top-nav__menu,
.top-nav__dropdown:focus-within .top-nav__menu,
.top-nav__dropdown.is-open .top-nav__menu {
  display: block;
}

.top-nav__menu li {
  position: relative;
  margin: 0;
  padding: 0;
  list-style: none;
}

.top-nav__menu-link {
  display: block;
  padding: var(--spacing-sm) var(--spacing-md);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  letter-spacing: var(--letter-spacing-caps);
  text-align: left;
  text-decoration: none;
  white-space: nowrap;
}

.top-nav__menu-link:hover {
  background: var(--surface-hover);
  color: var(--text-primary);
  text-decoration: none;
}

.top-nav__menu-link.router-link-active {
  background: var(--surface-selected);
  color: var(--text-primary);
  font-weight: var(--font-weight-bold);
}

/* Pozycja z podmenu — wskaźnik rozwinięcia w prawo. */
.top-nav__menu-link--parent {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
  cursor: pointer;
}

.top-nav__menu-link--parent::after {
  content: "›";
  color: var(--text-muted);
  font-size: var(--font-size-lg);
  line-height: 1;
}

.top-nav__submenu {
  position: absolute;
  top: 0;
  right: 100%;
  left: auto;
  z-index: 310;
  display: none;
  min-width: 200px;
  margin: 0;
  padding: var(--spacing-xs) 0;
  list-style: none;
  background: var(--surface-primary);
  border: 1px solid var(--border-default);
  border-radius: var(--panel-radius);
  box-shadow: var(--shadow-overlay);
}

.top-nav__submenu-parent:hover .top-nav__submenu,
.top-nav__submenu-parent:focus-within .top-nav__submenu {
  display: block;
}

/* Po wybraniu pozycji z menu chowamy je od razu — ta sama specyficzność co reguły
   :hover/:focus-within powyżej, więc wygrywa kolejnością. Flaga zdejmowana przy
   wyjściu kursora z zakładki (mouseleave) lub wejściu focusem (focusin). */
.top-nav__dropdown.is-dismissed .top-nav__menu,
.top-nav__dropdown.is-dismissed .top-nav__submenu {
  display: none;
}

/* --- przełącznik języka ---------------------------------------------------- */

.top-nav__lang {
  position: relative;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 64px;
  padding: 0 var(--spacing-md);
  background: var(--surface-secondary);
  cursor: pointer;
  user-select: none;
}

.top-nav--sg .top-nav__lang {
  margin-left: auto;
}

.top-nav__flag img,
.top-nav__lang-menu img {
  display: block;
  height: 20px;
  width: auto;
}

.top-nav__lang-menu {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 200;
  display: none;
  padding: 5.5px;
  background: var(--surface-primary);
  border: 1px solid var(--border-default);
  border-radius: 0 0 var(--panel-radius) var(--panel-radius);
  box-shadow: var(--shadow-overlay);
}

.top-nav__lang:hover .top-nav__lang-menu {
  display: block;
}

.top-nav__lang-menu > span {
  display: block;
  padding: var(--spacing-sm) var(--spacing-lg);
  cursor: pointer;
}

.top-nav__lang-menu > span:hover {
  background: var(--surface-hover);
}

</style>
